import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Appointment } from '@/context/types/PetTypes';
import type { Database } from '@/integrations/supabase/types';
import { useReminderSync } from '@/hooks/useReminderSync';
import { loadAppointments, saveAppointments } from '@/context/utils/localStorage';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

/**
 * Hook react-query pour le domaine "rendez-vous".
 *
 * Remplace le passage par le God `PetContext`. Reproduit la logique de
 * `context/hooks/useAppointmentContext.ts` : table `appointments`, migration
 * localStorage→Supabase (une fois), et synchro des rappels (useReminderSync).
 */

type ApptRow = Database['public']['Tables']['appointments']['Row'];

const mapRowToAppointment = (row: ApptRow): Appointment => ({
  id: row.id,
  petId: row.pet_id ?? undefined,
  title: row.title,
  date: row.date,
  time: row.time ?? undefined,
  location: row.location ?? undefined,
  notes: row.notes ?? undefined,
  isCompleted: row.is_completed ?? false,
  reminderEnabled: row.reminder_enabled ?? false,
  reminderTime: row.reminder_time ?? undefined,
  notifyBefore: row.notify_before ?? undefined,
  notifyUnit: (row.notify_unit ?? undefined) as Appointment['notifyUnit'],
  type: (row.type ?? undefined) as Appointment['type'],
  category: (row.category ?? undefined) as Appointment['category'],
});

const mapAppointmentToRow = (appt: Omit<Appointment, 'id'>, userId: string) => ({
  user_id: userId,
  pet_id: appt.petId ?? null,
  title: appt.title,
  date: appt.date,
  time: appt.time ?? null,
  location: appt.location ?? null,
  notes: appt.notes ?? null,
  is_completed: appt.isCompleted ?? false,
  reminder_enabled: appt.reminderEnabled ?? false,
  reminder_time: appt.reminderTime ?? null,
  notify_before: appt.notifyBefore ?? null,
  notify_unit: appt.notifyUnit ?? null,
  type: appt.type ?? null,
  category: appt.category ?? null,
});

export function useAppointments() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { syncReminder, deleteReminder } = useReminderSync();
  const queryKey = ['appointments', user?.id];

  const appointmentsQuery = useQuery({
    queryKey,
    enabled: !!user,
    queryFn: async (): Promise<Appointment[]> => {
      // 1) Migration localStorage → Supabase (une seule fois)
      const migrationKey = `appointments-migrated-${user!.id}`;
      if (localStorage.getItem(migrationKey) !== 'true') {
        const local = loadAppointments();
        if (local && local.length > 0) {
          const rows = local.map((a) => mapAppointmentToRow(a as Omit<Appointment, 'id'>, user!.id));
          await supabase.from('appointments').upsert(rows, { onConflict: 'id', ignoreDuplicates: true });
          saveAppointments([]);
        }
        localStorage.setItem(migrationKey, 'true');
      }
      // 2) Charger depuis Supabase
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('user_id', user!.id)
        .order('date', { ascending: true })
        .limit(200);
      if (error) throw error;
      return (data ?? []).map(mapRowToAppointment);
    },
  });

  const appointments = appointmentsQuery.data ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey });

  const addAppointment = async (appointment: Omit<Appointment, 'id'>) => {
    if (!user) return;
    const { data, error } = await supabase
      .from('appointments')
      .insert(mapAppointmentToRow(appointment, user.id))
      .select()
      .single();
    if (error) {
      logger.error('Erreur ajout rendez-vous:', error);
      toast.error("Impossible d'ajouter le rendez-vous");
      return;
    }
    const newAppt = mapRowToAppointment(data);
    invalidate();
    toast.success('Rendez-vous ajouté');
    if (newAppt.reminderEnabled) syncReminder(newAppt);
  };

  const updateAppointment = async (id: string, updates: Partial<Appointment>) => {
    if (!user) return;
    const payload: Database['public']['Tables']['appointments']['Update'] = {};
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.date !== undefined) payload.date = updates.date;
    if (updates.time !== undefined) payload.time = updates.time;
    if (updates.location !== undefined) payload.location = updates.location;
    if (updates.notes !== undefined) payload.notes = updates.notes;
    if (updates.isCompleted !== undefined) payload.is_completed = updates.isCompleted;
    if (updates.reminderEnabled !== undefined) payload.reminder_enabled = updates.reminderEnabled;
    if (updates.reminderTime !== undefined) payload.reminder_time = updates.reminderTime;
    if (updates.notifyBefore !== undefined) payload.notify_before = updates.notifyBefore;
    if (updates.notifyUnit !== undefined) payload.notify_unit = updates.notifyUnit;
    if (updates.type !== undefined) payload.type = updates.type;
    if (updates.category !== undefined) payload.category = updates.category;

    const { error } = await supabase
      .from('appointments')
      .update(payload)
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) {
      logger.error('Erreur mise à jour rendez-vous:', error);
      toast.error('Impossible de mettre à jour le rendez-vous');
      return;
    }
    const current = appointments.find((a) => a.id === id);
    if (current) syncReminder({ ...current, ...updates });
    invalidate();
    toast.success('Rendez-vous mis à jour');
  };

  const deleteAppointment = async (id: string) => {
    if (!user) return;
    const { error } = await supabase
      .from('appointments')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) {
      logger.error('Erreur suppression rendez-vous:', error);
      toast.error('Impossible de supprimer le rendez-vous');
      return;
    }
    invalidate();
    toast.success('Rendez-vous supprimé');
    deleteReminder(id);
  };

  return {
    appointments,
    isLoading: appointmentsQuery.isLoading,
    addAppointment,
    updateAppointment,
    deleteAppointment,
  };
}
